import { createContext, useContext } from 'react';

import { useQuery, useQueryClient, useMutation, QueryKeyHashFunction } from '@tanstack/react-query';

import { showError, showSuccess } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';

import { useIntegrationsApi } from './integrations-provider';
import type {
  Integration,
  IntegrationEndpoint,
  IntegrationEndpointVariable,
  IntegrationWebhook,
  DuplicateIntegrationData,
  IntegrationListOptions,
  ListQuery,
  ReorderIntegrationData,
} from './integrations-types';

const getIntegrationsQueryKey = (queryString?: string) =>
  ['integrations', queryString].filter(Boolean);
const integrationsQueryKey = ['integrations'];
const getIntegrationQueryKey = (shortId?: string) => ['integrations', shortId].filter(Boolean);
const getWebhookQueryKey = (shortId?: string) => ['integrations-webhook', shortId].filter(Boolean);
const getWebhookLogsQueryKey = (shortId?: string) =>
  ['integrations-webhook', shortId, 'logs'].filter(Boolean);
const getIntegrationEndpointQueryKey = (shortId?: string) =>
  ['integrations', 'endpoint', shortId].filter(Boolean);

export const useIntegration = (shortId: string) => {
  const { loadIntegration } = useIntegrationsApi();
  return useQuery({
    queryKey: getIntegrationQueryKey(shortId),
    queryFn: () => loadIntegration(shortId),
    enabled: Boolean(shortId),
  });
};

export const useInvalidateIntegrations = () => {
  const queryClient = useQueryClient();

  const invalidateIntegrations = () => queryClient.invalidateQueries(integrationsQueryKey);
  const invalidateIntegration = (shortId: string) =>
    queryClient.invalidateQueries(getIntegrationQueryKey(shortId));
  const invalidateWebhook = (shortId: string) =>
    queryClient.invalidateQueries(getWebhookQueryKey(shortId));
  const invalidateIntegrationEndpoint = (shortId: string) =>
    queryClient.invalidateQueries(getIntegrationEndpointQueryKey(shortId));

  return {
    invalidateIntegrations,
    invalidateIntegration,
    invalidateWebhook,
    invalidateIntegrationEndpoint,
  };
};

export const useIntegrationList = ({
  queryString = '',
  isTested = false,
}: IntegrationListOptions = {}) => {
  const { loadIntegrations } = useIntegrationsApi();

  return useQuery({
    queryKey: getIntegrationsQueryKey(queryString),
    queryFn: () => loadIntegrations(queryString, isTested),
    keepPreviousData: true,
  });
};

export const useIntegrationEndpoint = (
  shortId: string,
  options: { cacheTime?: number; queryKeyHashFn?: QueryKeyHashFunction<string[]> } = {}
) => {
  const { loadIntegrationEndpoint } = useIntegrationsApi();

  return useQuery({
    queryKey: ['integrations', 'endpoint', shortId],
    queryFn: () => loadIntegrationEndpoint(shortId),
    enabled: Boolean(shortId),
    ...options,
  });
};

export const useIntegrationWebhook = (shortId: string) => {
  const { loadIntegrationWebhook } = useIntegrationsApi();

  return useQuery({
    queryKey: ['integrations-webhook', shortId],
    queryFn: () => loadIntegrationWebhook(shortId),
    enabled: Boolean(shortId),
  });
};

export const useIntegrationWebhookLogs = (shortId: string) => {
  const { loadIntegrationWebhookLogs } = useIntegrationsApi();

  return useQuery({
    queryKey: getWebhookLogsQueryKey(shortId),
    queryFn: () => loadIntegrationWebhookLogs(shortId),
  });
};

const ListsScopeContext = createContext<{ templateId?: string }>({});
export const ListsScopeProvider = ({
  templateId,
  children,
}: {
  templateId?: string;
  children: React.ReactNode;
}) => <ListsScopeContext.Provider value={{ templateId }}>{children}</ListsScopeContext.Provider>;

export const useListsScope = () => useContext(ListsScopeContext);

export const useLists = (params?: ListQuery) => {
  const { loadLists } = useIntegrationsApi();
  const { templateId: scopedTemplateId } = useContext(ListsScopeContext);
  const data = typeof params === 'string' ? { search: params } : params ?? {};
  const templateId = data.templateId ?? scopedTemplateId;
  const search = data.search ?? '';

  return useQuery({
    queryKey: ['lists', templateId, search].filter(Boolean),
    queryFn: () => loadLists?.({ templateId, search }),
    keepPreviousData: true,
  });
};

export const useCreateIntegrationEndpoint = () => {
  const { createIntegrationEndpoint } = useIntegrationsApi();
  const { invalidateIntegration } = useInvalidateIntegrations();

  return useMutation({
    mutationFn: createIntegrationEndpoint,
    onSuccess: (data) => {
      if (data?.integration) {
        invalidateIntegration(data.integration.shortId);
      }
    },
  });
};

export const useUpdateIntegrationEndpoint = () => {
  const { updateIntegrationEndpoint } = useIntegrationsApi();
  const { invalidateIntegration, invalidateIntegrationEndpoint } = useInvalidateIntegrations();

  return useMutation({
    mutationFn: (params: { shortId: string; data: any }) =>
      updateIntegrationEndpoint(params.shortId, params.data),
    onSuccess: (data) => {
      const integrationId =
        typeof data.integration === 'string' ? data.integration : data.integration.shortId;
      invalidateIntegration(integrationId);
      invalidateIntegrationEndpoint(data.shortId);
    },
  });
};

export const useDeleteIntegrationEndpoint = (integrationShortId: string, shortId: string) => {
  const { deleteIntegrationEndpoint } = useIntegrationsApi();
  const { invalidateIntegration } = useInvalidateIntegrations();

  return useMutation({
    mutationFn: () => deleteIntegrationEndpoint(shortId),
    onSuccess: () => {
      invalidateIntegration(integrationShortId);
    },
  });
};

export const useUpdateIntegrationEndpointExampleFiles = () => {
  const { updateIntegrationEndpointExampleFiles } = useIntegrationsApi();
  const { invalidateIntegrations } = useInvalidateIntegrations();

  return useMutation({
    mutationFn: (params: { shortId: string; data: FormData }) =>
      updateIntegrationEndpointExampleFiles(params.shortId, params.data),
    onSuccess: () => {
      invalidateIntegrations();
    },
  });
};

export const useUpdateIntegrationEndpointDefaultFiles = () => {
  const { updateIntegrationEndpointDefaultFiles } = useIntegrationsApi();
  const { invalidateIntegrations } = useInvalidateIntegrations();

  return useMutation({
    mutationFn: (params: { shortId: string; data: FormData }) =>
      updateIntegrationEndpointDefaultFiles(params.shortId, params.data),
    onSuccess: () => {
      invalidateIntegrations();
    },
  });
};

export const useCreateIntegrationWebhook = () => {
  const { createIntegrationWebhook } = useIntegrationsApi();
  const { invalidateIntegration } = useInvalidateIntegrations();

  return useMutation({
    mutationFn: createIntegrationWebhook,
    onSuccess: (data) => {
      invalidateIntegration(data.integration);
    },
  });
};

export const useUpdateIntegrationWebhook = () => {
  const { updateIntegrationWebhook } = useIntegrationsApi();
  const { invalidateWebhook, invalidateIntegration } = useInvalidateIntegrations();

  return useMutation({
    mutationFn: (params: { shortId: string; data: any }) =>
      updateIntegrationWebhook(params.shortId, params.data),
    onSuccess: (data) => {
      invalidateWebhook(data.shortId);
      invalidateIntegration(data.integration);
    },
  });
};

export const useDeleteIntegrationWebhook = (shortId: string) => {
  const { deleteIntegrationWebhook } = useIntegrationsApi();
  const { invalidateWebhook, invalidateIntegrations } = useInvalidateIntegrations();

  return useMutation({
    mutationFn: (data?: { url: string; method: string; body?: string }) =>
      deleteIntegrationWebhook(shortId, data),
    onSuccess: () => {
      invalidateWebhook(shortId);
      invalidateIntegrations();
    },
  });
};

export const useUpdateIntegrationAuthentication = (shortId: string) => {
  const { updateIntegrationAuthentication } = useIntegrationsApi();
  const { invalidateIntegration } = useInvalidateIntegrations();
  const { t } = useScopedTranslate(locale);

  return useMutation({
    mutationFn: (data: any) => updateIntegrationAuthentication(shortId, data),
    onSuccess: () => {
      invalidateIntegration(shortId);
    },
    onError: (error: any) => {
      const data = error?.response?.data;
      const authErrors = data?.authentication?.nonFieldErrors;
      const nonFieldErrors = data?.nonFieldErrors;
      const message = authErrors?.[0] ?? nonFieldErrors?.[0] ?? t('invalidRequest');
      showError(message);
    },
  });
};

export const useIntegrationTestRequest = (shortId: string) => {
  const { integrationTestRequest } = useIntegrationsApi();
  const { invalidateIntegrations } = useInvalidateIntegrations();
  const { t } = useScopedTranslate(locale);
  return useMutation({
    mutationFn: (data: { variables: IntegrationEndpointVariable[] }) =>
      integrationTestRequest(shortId, data),
    onSuccess: () => {
      invalidateIntegrations();
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error ?? t('invalidRequest');
      showError(message);
    },
  });
};

export const useAuthenticationTest = () => {
  const { authenticationTest } = useIntegrationsApi();
  return useMutation({
    mutationFn: authenticationTest,
  });
};

export const useDuplicateIntegrationEndpoint = (endpoint: IntegrationEndpoint<string>) => {
  const { t } = useScopedTranslate(locale);
  const { duplicateIntegrationEndpoint } = useIntegrationsApi();
  const { invalidateIntegrations } = useInvalidateIntegrations();

  return useMutation({
    mutationFn: (data: { name: string }) => duplicateIntegrationEndpoint(endpoint.shortId, data),
    onSuccess: () => {
      showSuccess(t('integrationDuplicated'));
      invalidateIntegrations();
    },
  });
};

export const useDuplicateIntegrationWebhook = (webhook: IntegrationWebhook) => {
  const { t } = useScopedTranslate(locale);
  const { duplicateIntegrationWebhook } = useIntegrationsApi();
  const { invalidateIntegration } = useInvalidateIntegrations();

  return useMutation({
    mutationFn: (data: { name: string }) => duplicateIntegrationWebhook(webhook.shortId, data),
    onSuccess: () => {
      invalidateIntegration(webhook.integration);
      showSuccess(t('webhookDuplicated'));
    },
  });
};

export const useSubscribeIntegrationWebhook = (shortId: string) => {
  const { subscribeIntegrationWebhook } = useIntegrationsApi();
  const { invalidateWebhook, invalidateIntegrations } = useInvalidateIntegrations();

  return useMutation({
    mutationFn: (data: object) => subscribeIntegrationWebhook(shortId, data),
    onSuccess: () => {
      invalidateWebhook(shortId);
      invalidateIntegrations();
    },
  });
};

export const useUnsubscribeIntegrationWebhook = (shortId: string) => {
  const { unsubscribeIntegrationWebhook } = useIntegrationsApi();
  const { invalidateWebhook, invalidateIntegrations } = useInvalidateIntegrations();

  return useMutation({
    mutationFn: (data: any) => unsubscribeIntegrationWebhook(shortId, data),
    onSuccess: () => {
      invalidateWebhook(shortId);
      invalidateIntegrations();
    },
  });
};

export const useValidateJqQuery = () => {
  const { validateJqQuery } = useIntegrationsApi();
  return useMutation({
    mutationFn: validateJqQuery,
  });
};

export const useGenerateJqQuery = () => {
  const { generateJqQuery } = useIntegrationsApi();
  return useMutation({
    mutationFn: generateJqQuery,
  });
};

export const useCreateIntegration = () => {
  const { createIntegration } = useIntegrationsApi();
  const { invalidateIntegrations } = useInvalidateIntegrations();

  return useMutation({
    mutationFn: createIntegration,
    onSuccess: () => {
      invalidateIntegrations();
    },
  });
};

export const useEditIntegration = (onSuccess?: () => void) => {
  const { invalidateIntegrations } = useInvalidateIntegrations();
  const { updateIntegration } = useIntegrationsApi();

  return useMutation({
    mutationFn: updateIntegration,
    onSuccess: () => {
      invalidateIntegrations();
      onSuccess?.();
    },
  });
};

export const useDuplicateIntegration = (integration: Integration) => {
  const { t } = useScopedTranslate(locale);
  const { duplicateIntegration } = useIntegrationsApi();
  const { invalidateIntegrations } = useInvalidateIntegrations();

  return useMutation({
    mutationFn: (data: DuplicateIntegrationData) => duplicateIntegration(integration.shortId, data),
    onSuccess: () => {
      showSuccess(t('integrationDuplicated'));
      invalidateIntegrations();
    },
  });
};

export const useDeleteIntegration = () => {
  const { deleteIntegration } = useIntegrationsApi();
  const { invalidateIntegrations } = useInvalidateIntegrations();

  return useMutation({
    mutationFn: deleteIntegration,
    onSuccess: () => {
      invalidateIntegrations();
    },
  });
};

export const useReorderIntegration = (integrationShortId: string) => {
  const { reorderIntegration } = useIntegrationsApi();
  const { invalidateIntegration } = useInvalidateIntegrations();

  return useMutation({
    mutationFn: (data: ReorderIntegrationData) => reorderIntegration(integrationShortId, data),
    onSuccess: () => {
      invalidateIntegration(integrationShortId);
    },
  });
};

const locale = {
  en: {
    webhookDuplicated: 'Webhook duplicated',
    integrationDuplicated: 'Integration duplicated',
    invalidRequest: 'Invalid request. Please check the URL and try again.',
  },
};
